/**
 * Deploy-staleness handling for SPAs that serve hash-named bundles.
 *
 * A deploy rotates the hashed chunk filenames; a tab loaded before the deploy
 * still references the old names, and its next lazy `import()` 404s. Two
 * layers, no UI:
 *
 * - Proactive: sample version sources (on load, every 5 minutes, and when
 *   the tab regains focus) and compare each source's fingerprint against its
 *   first sample. A difference means a new deploy; the flag latches and the
 *   router glue (`appUpdateNavigationGuard`) escalates the next SPA
 *   navigation into a full document load — invisible to the user, since
 *   navigation was already discarding page state. The default source is the
 *   app's own `/index.html` asset-URL set; module-federation hosts add
 *   `manifestSource(url)` per remote, because a remote deploys independently
 *   of its host and the host's own HTML never reflects that.
 * - Reactive: when a chunk load actually fails (Vite's `vite:preloadError`,
 *   or a chunk-shaped error reaching an error boundary), reload once —
 *   guarded against loops and offline — landing on the URL the router has
 *   already committed, i.e. the page the user clicked.
 *
 * Contract with the web server (see the app's nginx config):
 * - `/index.html` is served with `Cache-Control: no-cache`, so every sample
 *   reflects the currently deployed version;
 * - hashed assets are immutable and 404 once rotated out.
 *
 * The asset-set comparison (rather than a stamped build id) is deliberate:
 * CI rebuilds every image on every release tag, so a stamp would flag
 * backend-only releases; the asset set only changes when the frontend does.
 * It is also inert under the Vite dev server, whose HTML has no hashed
 * assets.
 */

const VITE_PRELOAD_ERROR_EVENT = 'vite:preloadError';
const RELOAD_GUARD_STORAGE_KEY = 'app-update:last-chunk-reload';
const RELOAD_GUARD_HISTORY_KEY = 'appUpdateReloadStamp';
/** A reload within this window didn't fix the missing chunk — stop reloading
 * and let the error surface (the deployed version is genuinely broken). */
const RELOAD_GUARD_WINDOW_MS = 30_000;
/** Give the router time to commit the target URL before reloading, so the
 * reload lands on the page the user clicked (see installChunkReloadHandler). */
const CHUNK_RELOAD_DELAY_MS = 50;
const UPDATE_CHECK_INTERVAL_MS = 5 * 60_000;
/** Minimum gap between samples when the tab regains focus/visibility. */
const UPDATE_CHECK_MIN_GAP_MS = 60_000;

const ASSET_URL_RE = /(?:src|href)=["'](\/assets\/[^"']+)["']/g;

/** A version source samples one upstream and returns a content-derived
 * fingerprint, or null when it has no signal (a null must never become a
 * baseline nor count as a change). */
export type AppUpdateSource = () => Promise<string | null>;

let activeSources: AppUpdateSource[] | null = null;
/** Per-source first samples, parallel to activeSources. */
let baselines: (string | null)[] = [];
let updateAvailable = false;
/** True from the instant a chunk-failure reload is decided; gates Sentry
 * (`isReloadingForChunkError`) so the failure cascade isn't reported while
 * the page is being replaced. */
let chunkRecoveryActive = false;
let lastCheckAt = 0;
let checkInFlight: Promise<boolean> | null = null;

// window.location.reload() isn't observable under happy-dom; tests swap this.
let reloadPage = () => window.location.reload();

/** Order- and duplicate-insensitive fingerprint of the hashed asset URLs, or
 * null when the document carries none (dev server, edge challenge page) —
 * which must never become a baseline. */
function extractAssetUrls(html: string): string | null {
    const urls = Array.from(html.matchAll(ASSET_URL_RE), (m) => m[1]).filter(
        (url): url is string => url !== undefined,
    );
    if (urls.length === 0) return null;
    return [...new Set(urls)].sort().join('\n');
}

/** The default source: the app's own document. Its hashed asset references
 * change iff the frontend build changed — content-derived, so identical
 * rebuilds (backend-only release tags) never flag. */
export function indexHtmlSource(): AppUpdateSource {
    return async () => {
        const res = await fetch('/index.html', { cache: 'no-store' });
        const isHtml = res.headers.get('content-type')?.includes('text/html');
        if (!res.ok || !isHtml) return null;
        return extractAssetUrls(await res.text());
    };
}

/** Content-derived fingerprint of an mf-manifest: the exposes/shared chunk
 * lists are hashed filenames, so this is false-positive-free under any
 * remote-entry naming scheme (the entry's own stamp/[hash] rotates per build
 * while the bundler's emission is nondeterministic — see ionq-cloud-ui#1561).
 * Falls back to the entry name (per-build precision) if the lists are
 * missing. */
function fingerprintManifest(manifest: unknown): string | null {
    const files: string[] = [];
    const m = manifest as {
        exposes?: unknown;
        shared?: unknown;
        metaData?: { remoteEntry?: { name?: unknown } };
    };
    for (const group of [m?.exposes, m?.shared]) {
        if (!Array.isArray(group)) continue;
        for (const item of group) {
            const assets = (
                item as {
                    assets?: Record<
                        string,
                        { sync?: unknown; async?: unknown }
                    >;
                }
            )?.assets;
            for (const kind of [assets?.js, assets?.css]) {
                for (const list of [kind?.sync, kind?.async]) {
                    if (!Array.isArray(list)) continue;
                    for (const file of list) {
                        if (typeof file === 'string') files.push(file);
                    }
                }
            }
        }
    }
    if (files.length > 0) return [...new Set(files)].sort().join('\n');
    const name = m?.metaData?.remoteEntry?.name;
    return typeof name === 'string' && name !== '' ? name : null;
}

/** A module-federation remote's version pointer (its `mf-manifest.json` —
 * served no-cache + CORS). For hosts: a remote deploys independently, and
 * the host's own HTML never reflects it.
 * Plain GET on purpose: the response is no-cache, so the browser revalidates
 * with its own conditional headers; an author-set If-None-Match on a
 * cross-origin fetch would trigger a CORS preflight the remote's server
 * doesn't answer. */
export function manifestSource(url: string): AppUpdateSource {
    return async () => {
        const res = await fetch(url);
        if (!res.ok) return null;
        return fingerprintManifest(await res.json());
    };
}

async function sampleSources(): Promise<boolean> {
    lastCheckAt = Date.now();
    activeSources ??= [indexHtmlSource()];
    const sources = activeSources;
    const fingerprints = await Promise.all(
        // A failed sample (rejection or sync throw) is never an error
        // condition; try again next cycle.
        sources.map(async (source) => {
            try {
                return await source();
            } catch {
                return null;
            }
        }),
    );
    fingerprints.forEach((fingerprint, i) => {
        if (fingerprint === null) return;
        baselines[i] ??= fingerprint;
        // Latch forever: rolling deploys serve mixed replicas, so later
        // samples may match the baseline again without meaning "no update".
        if (fingerprint !== baselines[i]) updateAvailable = true;
    });
    return updateAvailable;
}

/** Sample every registered version source once (single-flight) and report
 * whether a new deploy has been detected since this tab loaded. */
export function checkForAppUpdate(): Promise<boolean> {
    checkInFlight ??= sampleSources().finally(() => {
        checkInFlight = null;
    });
    return checkInFlight;
}

export function isAppUpdateAvailable(): boolean {
    return updateAvailable;
}

/**
 * Start the proactive sampling. The app passes the gate — typically
 * `{ enabled: import.meta.env.PROD }` — so this package never reads build
 * flags itself and stays inert in dev servers and unit tests. `sources`
 * defaults to the app's own document (`indexHtmlSource()`); federation
 * hosts add a `manifestSource(url)` per remote. Any source's change latches.
 * Returns a stop function.
 */
export function startAppUpdatePolling({
    enabled,
    sources,
}: {
    enabled: boolean;
    sources?: AppUpdateSource[];
}): () => void {
    if (!enabled) return () => {};
    if (sources) {
        activeSources = sources;
        baselines = [];
    }
    const check = () => {
        // Latched: nothing left to learn.
        if (updateAvailable) return;
        void checkForAppUpdate();
    };
    const onVisible = () => {
        if (document.hidden) return;
        if (Date.now() - lastCheckAt < UPDATE_CHECK_MIN_GAP_MS) return;
        check();
    };
    // Establish the baseline as close to page load as possible: a deploy
    // landing before the first successful sample is invisible to the
    // comparison (the reactive layer still covers that window).
    check();
    const interval = setInterval(() => {
        // Only sample when someone could act on the result.
        if (document.hidden) return;
        check();
    }, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onVisible);
    };
}

const CHUNK_LOAD_ERROR_PATTERNS = [
    /Failed to fetch dynamically imported module/, // Chrome, Edge
    /error loading dynamically imported module/i, // Firefox
    /Importing a module script failed/, // Safari
    /Unable to preload CSS for/, // Vite's preload helper
];

/** A failed lazy chunk/CSS load — the stale-deploy failure signature. */
export function isChunkLoadError(error: unknown): boolean {
    return (
        error instanceof Error &&
        CHUNK_LOAD_ERROR_PATTERNS.some((p) => p.test(error.message))
    );
}

// history.state also survives location.reload() (same history entry) and
// works when sessionStorage is blocked — without a persisted stamp, a
// broken deploy would reload endlessly (the in-memory flag resets per page
// load). Merged, never replaced: react-router owns history.state (its
// idx/usr keys), and clobbering it breaks back/forward restoration. The key
// only has to survive the ~50ms between decision and reload, so a later
// router navigation dropping it is fine.
function readHistoryStamp(): number {
    try {
        const state = window.history.state as Record<string, unknown> | null;
        const value = state?.[RELOAD_GUARD_HISTORY_KEY];
        return typeof value === 'number' ? value : 0;
    } catch {
        return 0;
    }
}

function writeHistoryStamp(now: number): void {
    try {
        const state = window.history.state;
        const base = typeof state === 'object' && state !== null ? state : {};
        window.history.replaceState(
            { ...base, [RELOAD_GUARD_HISTORY_KEY]: now },
            '',
        );
    } catch {
        // Best effort, like sessionStorage below.
    }
}

function readGuardStamp(): number {
    let stored = 0;
    try {
        stored = Number(sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY)) || 0;
    } catch {
        // Storage unavailable: the history.state stamp still guards.
    }
    return Math.max(stored, readHistoryStamp());
}

// sessionStorage first (per-tab, survives the reload it guards), plus the
// history.state fallback so the loop guard outlives blocked storage too.
function writeGuardStamp(now: number): void {
    try {
        sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, String(now));
    } catch {
        // Storage unavailable: history.state carries the stamp.
    }
    writeHistoryStamp(now);
}

function canAttemptReload(): boolean {
    // Offline: the same chunk fetch would fail again — surface the error.
    if (navigator.onLine === false) return false;
    return Date.now() - readGuardStamp() >= RELOAD_GUARD_WINDOW_MS;
}

function beginChunkRecovery(): void {
    chunkRecoveryActive = true;
    writeGuardStamp(Date.now());
}

/**
 * Reload once for a missing-chunk failure. 'suppressed' when a recovery is
 * already in flight, 'blocked' when reloading can't help (offline, or the
 * previous reload — less than RELOAD_GUARD_WINDOW_MS ago — didn't fix it).
 */
export function tryReloadForChunkError():
    | 'reloading'
    | 'suppressed'
    | 'blocked' {
    if (chunkRecoveryActive) return 'suppressed';
    if (!canAttemptReload()) return 'blocked';
    beginChunkRecovery();
    reloadPage();
    return 'reloading';
}

/** Sentry `beforeSend` gate: drop events while a chunk-failure reload is in
 * flight — the reload is the remediation; the cascade is noise. */
export function isReloadingForChunkError(): boolean {
    return chunkRecoveryActive;
}

/** For error-boundary fallbacks: true when the error is (part of) a
 * chunk-failure recovery and the fallback should render a spinner instead of
 * an error box. Initiates the guarded reload if layer one hasn't already. */
export function decideChunkErrorRecovery(error: unknown): boolean {
    if (chunkRecoveryActive) return true;
    if (!isChunkLoadError(error)) return false;
    return tryReloadForChunkError() === 'reloading';
}

/**
 * Layer one: Vite dispatches this event when a dynamic import (or one of its
 * preloaded deps) fails to load. Marking recovery synchronously is what lets
 * the Sentry gate drop the whole cascade; the reload itself is deferred a
 * tick so React Router first commits the target URL — the reload then lands
 * on the page the user clicked, not the one they left. The rejection still
 * propagates (no preventDefault): the router renders its error fallback for
 * the split second behind the reload, which shows a spinner via
 * `decideChunkErrorRecovery`.
 */
export function installChunkReloadHandler(): () => void {
    let pendingReload: ReturnType<typeof setTimeout> | null = null;
    const onPreloadError = (event: Event) => {
        // Vite fires this for ANY rejected import() it wraps — a down
        // federated remote included, which hosts boot without. Only a
        // chunk-shaped failure is staleness a reload can fix; reloading for
        // anything else just races the boot's own navigations.
        const { payload } = event as Event & { payload?: unknown };
        if (!isChunkLoadError(payload)) return;
        if (chunkRecoveryActive || !canAttemptReload()) return;
        beginChunkRecovery();
        pendingReload = setTimeout(reloadPage, CHUNK_RELOAD_DELAY_MS);
    };
    window.addEventListener(VITE_PRELOAD_ERROR_EVENT, onPreloadError);
    return () => {
        window.removeEventListener(VITE_PRELOAD_ERROR_EVENT, onPreloadError);
        // Uninstall is a teardown API (tests): a decided-but-unfired reload
        // dies with the handler rather than firing after teardown.
        if (pendingReload !== null) clearTimeout(pendingReload);
    };
}

export function setReloadPageForTests(fn: () => void): void {
    reloadPage = fn;
}

export function resetAppUpdateStateForTests(): void {
    activeSources = null;
    baselines = [];
    updateAvailable = false;
    chunkRecoveryActive = false;
    lastCheckAt = 0;
    checkInFlight = null;
    reloadPage = () => window.location.reload();
}
